import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Fade,
  Paper,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { useSetAtom } from 'jotai';
import TreePane from './Components/TreePane';
import ConfigPane from './Components/ConfigPane';
import JsonPreviewPane from './Components/JsonPreviewPane';
import { openSnackbarAdviseAtom } from '../../../../Atoms';
import { resetSchemaAtom, importSchemaAtom } from './store/schemaAtoms';
import {
  FullSizeColumn,
  FullSizeFlexBox,
} from '../../../../Components/FlexBox';
import Header from '../../../../Components/ContactListWrapper/EntriesWrapper/Header';
import BackButton from '../BackButton';

const SchemaBuilder = function SchemaBuilder({
  show = false,
  close = () => {},
  onSave,
  data,
}) {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });
  const openSnackbar = useSetAtom(openSnackbarAdviseAtom);
  const reset = useSetAtom(resetSchemaAtom);
  const importSchema = useSetAtom(importSchemaAtom);

  const [previewExpanded, setPreviewExpanded] = useState(false);

  useEffect(() => {
    if (show) {
      if (data && data.uiSchemaConfiguration) {
        const result = importSchema(data.uiSchemaConfiguration);
        if (!result.ok) {
          console.error('Failed to load existing schema:', result.error);
          openSnackbar({
            severity: 'error',
            message: `${t('SCHEMA_LOAD_ERROR')}: ${result.error}`,
          });
          reset();
        }
      } else {
        reset();
      }
      setPreviewExpanded(false);
    }
  }, [show, data, reset, importSchema, openSnackbar]);

  const handleSave = (jsonString) => {
    if (onSave) {
      onSave({ ...data, uiSchemaConfiguration: jsonString });
      return;
    }
    openSnackbar({
      severity: 'success',
      message: t('SCHEMA_READY_TO_SAVE'),
    });
  };

  return (
    <Fade in={show} unmountOnExit>
      <div style={{ display: show ? 'block' : 'none', height: '100%' }}>
        <Paper
          id="schema-builder-paper"
          sx={{
            width: '100%',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          <FullSizeColumn>
            <Header
              hideSearchBar
              hideExportExcel
              hideSorting
              hideFilters
              title="SCHEMA_BUILDER"
              caption={<BackButton label="WAVE_FILTERS" onClick={close} />}
            />
            <Divider />
            <FullSizeFlexBox sx={{ overflow: 'hidden' }}>
              <Box
                sx={{
                  width: previewExpanded ? '0%' : '24%',
                  minWidth: previewExpanded ? 0 : 260,
                  opacity: previewExpanded ? 0 : 1,
                  overflow: 'hidden',
                  transition: 'all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1)',
                }}
              >
                <Box sx={{ height: '100%' }}>
                  <TreePane />
                </Box>
              </Box>
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  opacity: previewExpanded ? 0 : 1,
                  transition: 'opacity 0.5s cubic-bezier(0.25, 0.8, 0.25, 1)',
                }}
              />
              <Box
                sx={{
                  flex: previewExpanded ? 0 : 1,
                  width: previewExpanded ? '0%' : 'auto',
                  minWidth: previewExpanded ? 0 : 320,
                  opacity: previewExpanded ? 0 : 1,
                  overflow: 'hidden',
                  transition: 'all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1)',
                }}
              >
                <Box sx={{ width: '100%', minWidth: 320, height: '100%' }}>
                  <ConfigPane />
                </Box>
              </Box>

              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  opacity: previewExpanded ? 0 : 1,
                  transition: 'opacity 0.5s cubic-bezier(0.25, 0.8, 0.25, 1)',
                }}
              />
              <Box
                sx={{
                  flex: previewExpanded ? 1 : 'none',
                  width: previewExpanded ? '100%' : '45%',
                  minWidth: 360,
                  position: 'relative',
                  transition: 'all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1)',
                }}
              >
                <Tooltip
                  title={
                    previewExpanded
                      ? t('SCHEMA_COLLAPSE_PREVIEW')
                      : t('SCHEMA_EXPAND_PREVIEW')
                  }
                  placement="left"
                >
                  <IconButton
                    onClick={() => setPreviewExpanded(!previewExpanded)}
                    size="small"
                    sx={{
                      position: 'absolute',
                      left: previewExpanded ? 16 : -14,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: '#ffffff',
                      border: '1px solid rgba(0,0,0,0.12)',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      zIndex: 10,
                      transition: 'left 0.5s cubic-bezier(0.25, 0.8, 0.25, 1)',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                    }}
                  >
                    {previewExpanded ? (
                      <ChevronRightRoundedIcon
                        fontSize="small"
                        sx={{ color: '#5A5A5A' }}
                      />
                    ) : (
                      <ChevronLeftRoundedIcon
                        fontSize="small"
                        sx={{ color: '#5A5A5A' }}
                      />
                    )}
                  </IconButton>
                </Tooltip>

                <JsonPreviewPane onSave={handleSave} data={data} />
              </Box>
            </FullSizeFlexBox>
          </FullSizeColumn>
        </Paper>
      </div>
    </Fade>
  );
};

export default SchemaBuilder;
